import React, { useEffect, useState, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import { useSortBy, useTable } from "react-table";
import DashboardCard from "./cards/DashboardCard";
import { apiReports } from "../api/reports";
import Sparkline from "./Sparkline";
import InspectorUtilisationDetailModal from "./InspectorUtilisationDetailModal";
import AreaUtilisationDetailModal from "./AreaUtilisationDetailModal";
import InspectorRouteMap, { preloadRouteMap } from "./InspectorRouteMap";

const green = "#94ba33";
const amber = "rgba(251,191,36)";
const red = "#d71433";
const blue = "#00acdc";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const utilColor = (u) => u >= 0.7 ? green : u >= 0.4 ? amber : red;

function UtilisationTable({ data, onRowClick }) {
  const columns = useMemo(
    () => [
      { Header: "Inspector", accessor: "name" },
      { Header: "Area", accessor: "workAreaName", Cell: ({ value }) => value || "—" },
      { Header: "Sched days", accessor: "daysScheduled", sortType: "basic" },
      { Header: "Idle", accessor: "idleDays", sortType: "basic" },
      { Header: "Capacity", accessor: "capacity", sortType: "basic" },
      { Header: "Actual", accessor: "actual", sortType: "basic" },
      {
        Header: "Util %",
        accessor: "capacityUtilisation",
        sortType: "basic",
        Cell: ({ value }) => 
          <span style={{ color: utilColor(value), fontWeight: 600 }}>{pct(value)}</span>
        ,
      },
      {
        Header: "Trend",
        accessor: "weekly",
        disableSortBy: true,
        Cell: ({ value }) =>
          value && value.length > 1 ? <Sparkline data={{ values: value }} color={blue} /> : "—",
      },
    ],
    []
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data, initialState: { sortBy: [{ id: "capacityUtilisation", desc: true }] } },
    useSortBy
  );
  return (
    <div className="tw-overflow-x-auto">
      <table {...getTableProps()} className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead>
          {headerGroups.map((hg, i) =>
            <tr key={i} {...hg.getHeaderGroupProps()} className="tw-bg-fs-dark-blue tw-bg-opacity-25">
              {hg.headers.map((col, j) =>
                <th key={j}
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs tw-cursor-pointer"
                >
                  {col.render("Header")}
                  {col.isSorted ? col.isSortedDesc ? " ▼" : " ▲" : ""}
                </th>
              )}
            </tr>
          )}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr key={i} {...row.getRowProps()} className={`${i % 2 ? "tw-bg-gray-100" : ""} tw-cursor-pointer hover:tw-bg-fs-dark-blue hover:tw-bg-opacity-10`}
                onClick={() => onRowClick && onRowClick(row.original)}
              >
                {row.cells.map((cell, j) =>
                  <td key={j} {...cell.getCellProps()} className="tw-px-4 tw-py-2 tw-text-sm">
                    {cell.render("Cell")}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const InspectorUtilisationTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [inspectorDetailId, setInspectorDetailId] = useState(null);
  const [inspectorUtilisation, setInspectorUtilisation] = useState(null);
  const [inspectorCapacity, setInspectorCapacity] = useState(null);
  const [areaDetailId, setAreaDetailId] = useState(null);
  const [areaCapacityWindow, setAreaCapacityWindow] = useState(null);
  const [areaUtilisation, setAreaUtilisation] = useState(null);
  const [routesTodayOpen, setRoutesTodayOpen] = useState(false);
  const [expandedMapUserId, setExpandedMapUserId] = useState(null);
  const todayStr = new Date().toISOString().split("T")[0];
  useEffect(() => { if (routesTodayOpen) { preloadRouteMap(); } }, [routesTodayOpen]);
  useEffect(() => {
    let cancelled = false;
    apiReports.getInspectorUtilisation(days)
      .then((res) => { if (!cancelled) { setData(res); setError(null); } })
      .catch((e) => { if (!cancelled) { setError(e); setData(null); } });
    return () => { cancelled = true; };
  }, [days]);

  const result = data?.data?.result;
  const loading = !data && !error;
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-py-12">
        <CircularProgress />
      </div>
    );
  }
  if (error || !result) {
    return <div className="tw-py-12 tw-text-center tw-text-fs-red">Failed to load utilisation data.</div>;
  }

  const s = result.summary;
  const inspectors = result.inspectors || [];
  const weekdays = result.weekdays || [];
  const areas = result.areas || [];
  const trend = result.trend || [];

  // Weekday capacity-vs-actual grouped bar
  const weekdayData = {
    labels: weekdays.map((w) => w.name),
    datasets: [
      {
        label: "Avg actual/day",
        data: weekdays.map((w) => Math.round(w.avgPerDay * 10) / 10),
        backgroundColor: blue,
      },
      {
        label: "Capacity/day",
        data: weekdays.map((w) => w.capacityPerDay),
        backgroundColor: amber,
      },
    ],
  };
  const weekdayOptions = {
    maintainAspectRatio: false,
    legend: { position: "top" },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Inspections / day" } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  // Trend line (team utilisation %)
  const fmtWeek = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? iso : `${d.getDate()} ${d.toLocaleString("en", { month: "short" })}`;
  };
  const trendData = {
    labels: trend.map((t) => fmtWeek(t.weekStart)),
    datasets: [
      {
        label: "Team utilisation %",
        data: trend.map((t) => Math.round((t.utilisation || 0) * 1000) / 10),
        borderColor: green,
        backgroundColor: "rgba(148,186,51,0.12)",
        borderWidth: 2,
        fill: true,
        lineTension: 0.25,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };
  const trendOptions = {
    maintainAspectRatio: false,
    legend: { position: "top" },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, callback: (v) => `${v}%` }, scaleLabel: { display: true, labelString: "Utilisation %" } }],
      xAxes: [{ ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 12 } }],
    },
    tooltips: {
      mode: "index",
      intersect: false,
      callbacks: {
        title: (items, data) => items && items[0] ? data.labels[items[0].index] : "",
        label: (item) => {
          const v = item && item.yLabel != null ? item.yLabel : 0;
          return `Team utilisation: ${Number(v).toFixed(1)}%`;
        },
      },
    },
  };

  return (
    <div>
      {/* Window selector */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <span className="tw-text-sm tw-text-gray-600">Window:</span>
        {[7, 30, 90].map((d) => 
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`tw-px-3 tw-py-1 tw-rounded-lg tw-text-sm ${
              days === d ? "tw-bg-primary tw-text-white" : "tw-bg-gray-200 tw-text-gray-700"
            }`}
          >
            Last {d} days
          </button>
        )}
      </div>

      {/* KPI cards */}
      <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
        <DashboardCard title="Capacity utilisation" value={pct(s.capacityUtilisation)} subtitle={`${s.totalCompletions.toLocaleString()} of ${s.totalCapacity.toLocaleString()} cap`} color="tw-bg-fs-green" />
        <DashboardCard title="Completions" value={s.totalCompletions} subtitle={`Capacity ${s.totalCapacity.toLocaleString()}`} color="tw-bg-primary" />
        <DashboardCard title="Idle scheduled days" value={s.idleScheduledDays} subtitle={`of ${s.totalScheduledDays} scheduled`} color="tw-bg-yellow-400" />
        <DashboardCard title="Inspectors on routes today" value={(s.inspectorsWithRoutesToday || 0).toLocaleString()} subtitle="click for the breakdown" color="tw-bg-fs-red" onClick={() => { setRoutesTodayOpen(!routesTodayOpen); setExpandedMapUserId(null); }} />
        <DashboardCard title="Days to clear backlog" value={s.daysInPipeline.toFixed(1)} subtitle="at current output" secondValue={"at 80%: " + (s.daysInPipelineAt80||0).toFixed(1) + "d  |  at 100%: " + (s.daysInPipelineAt100||0).toFixed(1) + "d"} color="tw-bg-fs-dark-blue" />
      </div>

      {/* Routes today — inline expandable section (click the card to toggle) */}
      {routesTodayOpen &&
        <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h3 className="tw-font-semibold">Inspectors on routes today ({(result.routesToday || []).length}) <span className="tw-text-xs tw-font-normal tw-text-gray-500">(click a row to expand its map)</span></h3>
            <button onClick={() => { setRoutesTodayOpen(false); setExpandedMapUserId(null); }} className="tw-text-gray-400 hover:tw-text-gray-600 tw-text-xl tw-leading-none">&times;</button>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3 tw-text-xs tw-text-gray-600">
            <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded" style={{ backgroundColor: "#2563eb" }} /> Not started</span>
            <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded" style={{ backgroundColor: "orange" }} /> Started</span>
            <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded" style={{ backgroundColor: "#00acdc" }} /> Completed</span>
            <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-inline-block tw-whitespace-nowrap tw-px-1.5 tw-rounded tw-text-[10px] tw-font-semibold tw-bg-amber-200 tw-text-amber-800">no route</span> planned but no route created</span>
          </div>
          {(result.routesToday || []).length === 0 ?
            <p className="tw-text-gray-500">No inspectors have planned routes today.</p>
            :
            <div className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead>
                  <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                    <th className="tw-px-2 tw-py-2 tw-w-8"></th>
                    <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Inspector</th>
                    <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Area</th>
                    <th className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Planned</th>
                    <th className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Done</th>
                    <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {result.routesToday.map((r, i) => {
                    const prog = r.planned > 0 ? Math.min(100, Math.round(r.done / r.planned * 100)) : 0;
                    const isOpen = expandedMapUserId === r.userId;
                    const noRoute = !r.hasRoute;
                    return (
                      <React.Fragment key={r.userId}>
                        <tr
                          className={`${noRoute ? "tw-bg-amber-50" : "tw-cursor-pointer hover:tw-bg-fs-dark-blue hover:tw-bg-opacity-10"} ${i % 2 ? "tw-bg-gray-100" : ""}`}
                          onClick={noRoute ? undefined : () => setExpandedMapUserId(isOpen ? null : r.userId)}
                        >
                          <td className="tw-px-2 tw-py-2 tw-text-center">
                            {noRoute
                              ? <span className="tw-inline-block tw-whitespace-nowrap tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold tw-bg-amber-200 tw-text-amber-800" title="Planned inspections but no route created for today">no route</span>
                              : <span className="tw-text-gray-500">{isOpen ? "▾" : "▸"}</span>}
                          </td>
                          <td className={`tw-px-4 tw-py-2 tw-text-sm ${noRoute ? "tw-text-gray-500" : ""}`}>{r.name}</td>
                          <td className={`tw-px-4 tw-py-2 tw-text-sm ${r.areaName === "Out of area" ? "tw-italic tw-text-gray-500" : ""}`}>{r.areaName}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm tw-text-right">{r.planned}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm tw-text-right">{r.done}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <div className="tw-flex-1 tw-h-2 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
                                <div className="tw-h-full tw-bg-fs-green" style={{ width: `${prog}%` }} />
                              </div>
                              <span className="tw-text-xs tw-text-gray-500 tw-w-9 tw-text-right">{prog}%</span>
                            </div>
                          </td>
                        </tr>
                        {isOpen && !noRoute &&
                          <tr>
                            <td colSpan={6} className="tw-p-3 tw-bg-gray-50">
                              <InspectorRouteMap inspectorId={r.userId} date={todayStr} />
                            </td>
                          </tr>
                        }
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          }
        </div>
      }

      {/* Trend chart — full width so a multi-point line is readable */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Team utilisation trend (weekly %)</h3>
        <div style={{ height: 320 }}>
          {trend.length > 1 ? <Line data={trendData} options={trendOptions} /> : <div className="tw-text-gray-500 tw-text-sm">Not enough data for a trend at this window.</div>}
        </div>
      </div>

      {/* Weekday capacity vs actual */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Weekday capacity vs actual</h3>
        <div style={{ height: 280 }}>
          <Bar data={weekdayData} options={weekdayOptions} />
        </div>
      </div>

      {/* Area utilisation */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Area utilisation <span className="tw-text-xs tw-font-normal tw-text-gray-500">(click a row for the county map)</span></h3>
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead>
              <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Area</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Inspectors</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Complete</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Outstanding</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Incoming</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Window capacity</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Utilisation</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Note</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((a, i) => 
                <tr key={a.workAreaId || `ua-${i}`} className={`${i % 2 ? "tw-bg-gray-100" : ""} ${a.unassigned ? "tw-bg-amber-50" : ""} tw-cursor-pointer hover:tw-bg-fs-dark-blue hover:tw-bg-opacity-10`}
                  onClick={() => { setAreaCapacityWindow(a.unassigned ? null : a.capacityWindow); setAreaUtilisation(a.unassigned ? null : a.utilisation); setAreaDetailId(a.workAreaId); }}
                >
                  <td className={`tw-px-4 tw-py-2 tw-text-sm ${a.unassigned ? "tw-italic tw-text-gray-500" : ""}`}>{a.name}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.unassigned ? "—" : a.inspectors}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.complete.toLocaleString()}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.outstanding.toLocaleString()}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.incoming.toLocaleString()}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.unassigned ? "—" : a.capacityWindow.toLocaleString()}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">
                    {a.unassigned ? "—" : a.noCoverage ? "—" : <span style={{ color: utilColor(a.utilisation), fontWeight: 600 }}>{pct(a.utilisation)}</span>}
                  </td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">
                    {a.unassigned ? <span className="tw-text-gray-500">counties not in any work area</span>
                      : a.noCoverage && <span className="tw-text-fs-red">work with no scheduled coverage</span>}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inspector table */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Inspector utilisation <span className="tw-text-xs tw-font-normal tw-text-gray-500">(click a row for detail)</span></h3>
        <UtilisationTable data={inspectors} onRowClick={(row) => { setInspectorUtilisation(row.capacityUtilisation); setInspectorCapacity(row.capacity); setInspectorDetailId(row.userId); }} />
      </div>

      <InspectorUtilisationDetailModal
        open={inspectorDetailId != null}
        onClose={() => { setInspectorDetailId(null); setInspectorUtilisation(null); setInspectorCapacity(null); }}
        userId={inspectorDetailId}
        days={days}
        utilisation={inspectorUtilisation}
        capacity={inspectorCapacity}
      />
      <AreaUtilisationDetailModal
        open={areaDetailId != null}
        onClose={() => { setAreaDetailId(null); setAreaCapacityWindow(null); setAreaUtilisation(null); }}
        workAreaId={areaDetailId}
        days={days}
        capacityWindow={areaCapacityWindow}
        utilisation={areaUtilisation}
      />

    </div>
  );
};

export default InspectorUtilisationTab;